import { compactLocalDate } from './date';
import {
  exportMigrations as defaultExportMigrations,
  migrateExportToCurrent,
  migrateToCurrent,
  migrations as defaultMigrations,
} from './migrate';
import { emptyProblemDrafts, type ProblemDrafts } from './problem-drafts';
import { validateExportFile } from './schema';
import {
  emptyLessonProgress,
  EXPORT_FORMAT,
  EXPORT_VERSION,
  type Drafts,
  type ExportFile,
  type LessonProgress,
  type ProgressState,
  type Settings,
} from './types';

/** エクスポートファイルを作る（progress-store.md §6.1）。 */
export function createExportFile(
  progress: ProgressState,
  settings: Settings,
  drafts: Drafts,
  now: Date,
  lessons: LessonProgress = emptyLessonProgress(),
  problemDrafts: ProblemDrafts = emptyProblemDrafts(),
): ExportFile {
  return {
    format: EXPORT_FORMAT,
    version: EXPORT_VERSION,
    exportedAt: now.toISOString(),
    progress,
    settings,
    drafts,
    lessons,
    problemDrafts,
  };
}

/** `inductor-class-YYYYMMDD.json`（端末ローカルの日付）。 */
export function exportFileName(now: Date): string {
  return `inductor-class-${compactLocalDate(now)}.json`;
}

/** `JSON.stringify(file, null, 2)`。 */
export function serializeExport(file: ExportFile): string {
  return JSON.stringify(file, null, 2);
}

export type ImportErrorKind =
  'invalid-json' | 'wrong-format' | 'newer-version' | 'schema' | 'backup-failed';

/** 取り込みの失敗の文言（§6.2）。schema は instancePath を添える。 */
export const IMPORT_ERROR_MESSAGES: Record<ImportErrorKind, string> = {
  'invalid-json': 'JSON として読めません',
  'wrong-format': 'inductor-class の書き出しファイルではありません',
  'newer-version': 'このアプリより新しい版で書き出されたファイルです',
  schema: '書き出しファイルの形が正しくありません',
  'backup-failed':
    '今の記録の控えをこの端末に保存できなかったので、読み込みを中止しました。先に書き出しで控えを取ってください',
};

export type ParseImportResult =
  | { ok: true; file: ExportFile; migratedFrom: number }
  | { ok: false; kind: ImportErrorKind; message: string; instancePath?: string };

function fail(kind: ImportErrorKind, instancePath?: string): ParseImportResult {
  const r: ParseImportResult = { ok: false, kind, message: IMPORT_ERROR_MESSAGES[kind] };
  if (instancePath !== undefined) {
    r.instancePath = instancePath;
    r.message = `${r.message}（${instancePath}）`;
  }
  return r;
}

const VALUE_KEYS = ['progress', 'settings', 'drafts', 'lessons', 'problemDrafts'] as const;

/**
 * 取り込むファイルを読む（§6.2 手順 1〜5）。状態の置き換えは store が行う。
 * ファイルの版は exportTable で、中の各値の版は table で移行する（§6.3）。
 */
export function parseImport(
  text: string,
  table: Readonly<Record<number, (v: unknown) => unknown>> = defaultMigrations,
  exportTable: Readonly<Record<number, (v: unknown) => unknown>> = defaultExportMigrations,
): ParseImportResult {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return fail('invalid-json');
  }
  if (
    typeof data !== 'object' ||
    data === null ||
    !('format' in data) ||
    data.format !== EXPORT_FORMAT
  ) {
    return fail('wrong-format');
  }
  const migrated = migrateExportToCurrent(data, exportTable);
  if (!migrated.ok) {
    return migrated.reason === 'newer-version' ? fail('newer-version') : fail('schema', '/version');
  }
  const file: Record<string, unknown> =
    typeof migrated.value === 'object' && migrated.value !== null ? { ...migrated.value } : {};
  for (const key of VALUE_KEYS) {
    if (!(key in file)) continue;
    const m = migrateToCurrent(file[key], table);
    if (!m.ok) {
      return m.reason === 'newer-version'
        ? fail('newer-version')
        : fail('schema', `/${key}/version`);
    }
    file[key] = m.value;
  }
  // problemDrafts（任意）は validateExportFile が schema の外で検証し、無ければ空を補う。
  const checked = validateExportFile(file);
  if (!checked.ok) return fail('schema', checked.instancePath);
  return { ok: true, file: checked.value, migratedFrom: migrated.from };
}

/** 最後のエクスポートから 7 日以上たったか（§6.1）。一度も書き出していなければ true。 */
export function shouldRemindExport(settings: Settings, now: Date): boolean {
  if (settings.lastExportAt === undefined) return true;
  const last = Date.parse(settings.lastExportAt);
  if (Number.isNaN(last)) return true;
  return now.getTime() - last >= 7 * 86_400_000;
}
